import PouchDB from 'pouchdb'
import UpsertPlugin from 'pouchdb-upsert'
import { fetchSH, HttpError } from '../api'
import auth from '../auth'

// Plugins
PouchDB.plugin(UpsertPlugin)

class DB {
  constructor(dbName = 'core', options = {}) {
    this.localDB = new PouchDB(dbName, options)
    this.sync = null
    this.id = null
  }

  initSync = async (options = {}, optionsTo = {}) => {
    this.remoteDB = new PouchDB(`${import.meta.env.VITE_COSS_URL}/sync/proxy`, {
      headers: { authorization: `Bearer ${auth.token.value}` },
    })
    try {
      const response = await fetchSH('sync/user', {
        method: 'POST',
        headers: { 'Content-Type': 'text/plain' },
      })

      this.id = response.id
      if (!this.id) {
        console.warn('initSync: response has no id, skipping remote sync')
        this.sync = null
        return null
      }

      await this.syncData(options, optionsTo)
      return this.id
    } catch (err) {
      if (err instanceof HttpError) {
        console.warn(
          'initSync failed, skipping remote sync:',
          err.status,
          err.message,
        )
        this.id = null
        this.sync = null
        return null
      }
      throw err
    }
  }

  doSync = async (direction, options = {}) => {
    const debugReplication = false
    const {
      onChange,
      onPause,
      onActive,
      onDenied,
      onCompleted,
      onError,
      ...otherOptions
    } = options

    const handlers = {
      change: onChange,
      pause: onPause,
      active: onActive,
      denied: onDenied,
      complete: onCompleted,
      error: onError,
    }

    const defaultHandler = (eventName) => (data) => {}

    const debugHandler = (eventName) => (data) => {
      console.debug('DB sync:' + eventName, direction, data)
    }

    const sync =
      direction === 'in'
        ? this.localDB.replicate.from(this.remoteDB, otherOptions)
        : this.localDB.replicate.to(this.remoteDB, otherOptions)

    return await Object.entries(handlers).reduce(
      (acc, [eventName, handler]) =>
        acc.on(eventName, (payload) => {
          handler ? handler(payload) : defaultHandler(eventName)(payload)

          if (debugReplication && import.meta.env.MODE === 'development') {
            debugHandler(eventName)(payload)
          }
        }),
      sync,
    )
  }

  syncData = async (options = {}, optionsTo = undefined) => {
    if (!this.id) {
      console.warn('No id provided')
      return
    }

    try {
      this.sync = await this.doSync('in', options)
      await this.doSync('out', optionsTo || options)
    } catch (error) {
      console.error('Sync failed:', error)
    }
  }

  cancelSync = () => {
    this.sync?.cancel()
  }

  createOrUpdate = async (data) => {
    if ('_id' in data) {
      let doc
      const upsert = async (data, oldDoc = {}) =>
        await this.withTimeout(
          this.localDB.upsert(data['_id'], () => ({
            ...data,
            _rev: oldDoc?._rev,
          })),
        )

      try {
        const oldDoc = await this.withTimeout(this.localDB.get(data['_id']))
        doc = await upsert(data, oldDoc)
      } catch (error) {
        if (error.name === 'not_found') {
          doc = await upsert(data)
        } else {
          console.error(error)
        }
      }

      return doc
    }
  }

  getData = async (id, options = {}) => {
    const data = await this.withTimeout(this.localDB.get(id, options))

    return data
  }

  getAllData = async (bulkIds) => {
    const allData = await this.withTimeout(this.localDB.allDocs(bulkIds))

    return {
      ...allData,
      rows: allData.rows.filter(
        (record) => (record.doc && !record.doc.deleted) || !record.doc,
      ),
    }
  }

  removeData = async (id, rev = undefined) => {
    // Revision deletion
    if (rev) {
      const remove = await this.withTimeout(this.localDB.remove(id, rev))

      return remove
    }

    // Full doc deletion, check for open leaf revisions and delete them
    const openRevisions = await this.getData(id, {
      include_docs: true,
      open_revs: 'all',
    })
    const activeRevisions = openRevisions.filter(
      (revision) => revision.ok && !revision.ok._deleted,
    )

    if (activeRevisions.length === 0) {
      return { ok: true, deleted: 0 }
    }

    const docsToDelete = activeRevisions.map((revision) => ({
      ...revision.ok,
      _deleted: true,
    }))

    const deleteResults = await this.localDB.bulkDocs(docsToDelete)

    return {
      ok: true,
      deleted: deleteResults.length,
      results: deleteResults,
    }
  }

  onChanges = (options, callback) => {
    this.localDB.changes(options).on('change', (data) => callback(data))
  }

  withTimeout = (promise, timeoutMs = 1000) => {
    const timeout = new Promise((_, reject) =>
      setTimeout(
        () => reject(new Error('Operation timeout exceeded')),
        timeoutMs,
      ),
    )

    return Promise.race([promise, timeout])
  }
}

export default DB
